import { useLazyQuery } from "@apollo/client";
import Add from "@mui/icons-material/Add";
import Download from "@mui/icons-material/Download";
import LinkOff from "@mui/icons-material/LinkOff";
import { Button, Paper, Stack, Tooltip, Typography } from "@mui/material";
import { GridActionsCellItem, GridColDef } from "@mui/x-data-grid-premium";
import { useAlerts } from "components/Alerts/AlertProvider";
import { DataGrid } from "components/DataGrid";
import { prettyPrintDocumentType } from "encamp-shared/src/utils/prettyPrintDocumentType";
import { CustomWorkDetailFieldsFragment } from "generated-graphql/graphql";
import { DateTime } from "luxon";
import GET_DOCUMENT_DOWNLOAD_LINK_QUERY from "queries/getDocumentDownloadLink";
import { downloadFromUrl } from "util/download";

type WorkDocument = CustomWorkDetailFieldsFragment["documents"][number];

type WorkDocumentsPanelProps = {
  documents: WorkDocument[];
  canEdit: boolean;
  onAddDocument: () => void;
  /** Detach a workflow-level attachment (step attachments detach from their step). */
  onDetach: (documentId: string) => void;
};

/**
 * The deliverable's documents tab: every attachment in one grid —
 * workflow-level ones plus each step's — with where it hangs, who attached
 * it, and when. Adding here attaches at the workflow level; per-step
 * attachments are made from the step cards.
 */
/**
 * Grid columns for the attachments table. A module-scope factory rather than an
 * inline array so the panel component stays layout — the actions column is the
 * only part that needs the caller's handlers.
 */
function documentColumns({
  canEdit,
  onDownload,
  onDetach,
}: {
  canEdit: boolean;
  onDownload: (documentId: string) => void;
  onDetach: (documentId: string) => void;
}): GridColDef<WorkDocument>[] {
  return [
    { field: "title", headerName: "Title", flex: 1.4 },
    {
      field: "documentType",
      headerName: "Type",
      flex: 0.8,
      valueFormatter: (value) => (value ? prettyPrintDocumentType(value) : ""),
    },
    {
      field: "stepDescription",
      headerName: "Attached to",
      flex: 1,
      valueGetter: (_, row) => row.stepDescription ?? "Deliverable",
    },
    {
      field: "attachedAt",
      headerName: "Attached",
      flex: 0.7,
      valueFormatter: (value) =>
        value ? DateTime.fromISO(value).toLocaleString(DateTime.DATE_MED) : "",
    },
    { field: "attachedByEmail", headerName: "By", flex: 0.9 },
    {
      field: "actions",
      type: "actions",
      resizable: false,
      getActions: ({ row }) => {
        const actions = [
          <Tooltip title="Download" key="download">
            <GridActionsCellItem
              label="Download"
              icon={<Download />}
              onClick={() => onDownload(row.documentId)}
            />
          </Tooltip>,
        ];
        if (canEdit && row.activityId === null) {
          actions.push(
            <Tooltip title="Detach from this deliverable (keeps the document)" key="detach">
              <GridActionsCellItem
                label="Detach"
                icon={<LinkOff />}
                onClick={() => onDetach(row.documentId)}
              />
            </Tooltip>
          );
        }
        return actions;
      },
    },
  ];
}

export function WorkDocumentsPanel({
  documents,
  canEdit,
  onAddDocument,
  onDetach,
}: WorkDocumentsPanelProps) {
  const alerts = useAlerts();
  const [getDocumentDownloadLink] = useLazyQuery(GET_DOCUMENT_DOWNLOAD_LINK_QUERY, {
    onCompleted: (data) => {
      if (!data?.getDocumentDownloadLink) {
        alerts.error("Error fetching download link");
        return;
      }
      // The signed URL downloads (attachment disposition); an anchor click
      // needs no popup allowance, unlike window.open outside the gesture.
      downloadFromUrl(data.getDocumentDownloadLink);
    },
    onError: (error) => {
      alerts.error("Error fetching download link", error);
    },
  });

  const columns = documentColumns({
    canEdit,
    onDownload: (documentId) => void getDocumentDownloadLink({ variables: { id: documentId } }),
    onDetach,
  });

  return (
    <Paper variant="outlined" sx={{ p: 2.5 }}>
      <Stack direction="row" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="h6" sx={{ flex: 1 }}>
          Documents
        </Typography>
        {canEdit && (
          <Button size="small" variant="outlined" startIcon={<Add />} onClick={onAddDocument}>
            Add document
          </Button>
        )}
      </Stack>
      {documents.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          Nothing attached yet — documents added here belong to the deliverable as a whole;
          step-specific files attach from their step card.
        </Typography>
      ) : (
        <DataGrid
          rows={documents}
          columns={columns}
          getRowId={(row) => `${row.documentId}:${row.activityId ?? "workflow"}`}
          hideFooter
          disableRowSelectionOnClick
          sx={{
            "& .MuiDataGrid-virtualScroller": { minHeight: "50px" },
          }}
        />
      )}
    </Paper>
  );
}
